import { css } from '@emotion/react';
import { CalloutBanner, PromoBannerBadge } from '@frontend/high-value-adoption';
import { useTranslation } from '@frontend/i18n';
import { InsuranceEligibilityFeatureCallout } from '@frontend/insurance-feature-callout#svg';
import { useHasWIVOrEIVAccess } from '@frontend/insurance-verification';
import { useEligibility } from '@frontend/user-preferences';
import { useNavigate } from '@frontend/weave-router';
import { theme } from '@frontend/theme';
import { Button, Text } from '@frontend/design-system';
import {
  InsuranceEligibilityCalloutDefaultCriteria,
  InsuranceEligibilityCalloutFeatureIds,
  InsuranceEligibilityCalloutTrackingIds,
} from './constants';

export const InsuranceEligibilityCalendarCallout = () => {
  const navigate = useNavigate();
  const { t } = useTranslation('schedule');
  const { isEIVFeatureHiddenInAllLocation } = useHasWIVOrEIVAccess();
  const { shouldShow, disableEligibility } = useEligibility({
    featureId: InsuranceEligibilityCalloutFeatureIds.calendar,
    defaultCriteria: InsuranceEligibilityCalloutDefaultCriteria,
  });

  const hasEligibilityListAccess = !isEIVFeatureHiddenInAllLocation;

  if (!hasEligibilityListAccess || !shouldShow) {
    return null;
  }

  return (
    <div css={styles.floatingContainer}>
      <CalloutBanner
        shouldShowArrow={false}
        color='seaweed'
        title={
          <div css={styles.titleRow}>
            <div css={styles.titleLeft}>
              <PromoBannerBadge badgeType='new' />
              <Text weight='bold' size='large'>
                {t('Insurance Eligibility in One Place')}
              </Text>
            </div>
            <Button
              variant='secondary'
              size='small'
              iconName='x'
              onClick={disableEligibility}
              trackingId={InsuranceEligibilityCalloutTrackingIds.scheduleCalendarInsuranceEligibilityCalloutDismiss}
              aria-label={t('Close')}
            />
          </div>
        }
        description={
          <div css={styles.descriptionContainer}>
            <Text size='medium'>
              {t(
                'Everything you need in one place - eligibility status, coverage details, and full eligibility reports in one easy-to-use list.'
              )}
            </Text>
            <div css={styles.imageContainer}>
              <InsuranceEligibilityFeatureCallout width={348} />
            </div>
          </div>
        }
        actions={
          <div css={styles.actionsRow}>
            <Button variant='tertiary' onClick={disableEligibility}>
              {t('Maybe Later')}
            </Button>
            <Button
              variant='primary'
              onClick={() => {
                navigate({ to: '/schedule/eligibility-list' });
                disableEligibility();
              }}
              trackingId={InsuranceEligibilityCalloutTrackingIds.scheduleCalendarInsuranceEligibilityCalloutOpen}
            >
              {t('Open Insurance Eligibility')}
            </Button>
          </div>
        }
        cardStyles={styles.card}
      />
    </div>
  );
};

const styles = {
  floatingContainer: css({
    position: 'fixed',
    right: theme.spacing(10),
    bottom: theme.spacing(4),
    zIndex: theme.zIndex.popover,
    maxWidth: '380px',
    width: 'min(380px, calc(100vw - 16px))',
  }),
  titleRow: css({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    backgroundColor: theme.colorsV2.seaweed50(10),
    padding: theme.spacing(2),
  }),
  titleLeft: css({
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    minWidth: 0,
  }),
  descriptionContainer: css({
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.5),
    padding: theme.spacing(2, 2, 0, 2),
  }),
  imageContainer: css({
    border: `1px solid ${theme.colors.neutral20}`,
    borderRadius: theme.borderRadius.medium,
    overflow: 'hidden',
    backgroundColor: theme.colors.white,
  }),
  card: css({
    width: '100%',
    backgroundColor: theme.colors.white,
    border: `1px solid ${theme.colors.secondary.seaweed40}`,
    borderRadius: theme.borderRadius.medium,
    overflow: 'hidden',
    padding: 0,
    gap: 0,
  }),
  actionsRow: css({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: theme.spacing(1),
    padding: theme.spacing(1.5, 2, 2, 2),
  }),
};
